import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Button from '../../Common/Button/Button';
import styles from './HeadlessSection.module.scss';
import homeStyles from '../../../styles/Home.module.scss';
import { DashboardMockup } from './DashboardMockup';

interface Feature {
  label: string;
  icon: string;
}

const features: Feature[] = [
  { label: 'AI Co-pilots', icon: 'ai' },
  { label: 'Advance Dashboards & MIS Reporting', icon: 'dashboard' },
  { label: 'Content Management', icon: 'content' },
  { label: 'Smart Inventory Management', icon: 'inventory' },
  { label: 'Order Management', icon: 'order' },
  { label: 'Offers and Promotion Management', icon: 'offers' },
  { label: 'Logistic Management', icon: 'logistics' },
  { label: 'Customer Relationship Management', icon: 'crm' },
  { label: 'Mobile Friendly Store Front & Point of Sales', icon: 'pos' },
  { label: 'Multi Shop & Marketplace Enablements', icon: 'marketplace' },
];

const featureIcons: Record<string, React.ReactNode> = {
  ai: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M9 3v2M15 3v2M9 19v2M15 19v2M3 9h2M3 15h2M19 9h2M19 15h2" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  content: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>
  ),
  inventory: (
    <>
      <path d="M12 3 4 7v10l8 4 8-4V7z" />
      <path d="M4 7l8 4 8-4M12 11v10" />
    </>
  ),
  order: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M9.5 4V3h5v1" />
      <path d="M9 10h6M9 14h6" />
    </>
  ),
  offers: (
    <>
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
      <path d="M6 18 18 6" />
    </>
  ),
  logistics: (
    <>
      <path d="M3 6h11v9H3z" />
      <path d="M14 9h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17.5" cy="18" r="1.6" />
    </>
  ),
  crm: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 6.5a3 3 0 0 1 0 5.6" />
      <path d="M20.5 20a5.6 5.6 0 0 0-4-5.3" />
    </>
  ),
  pos: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18M7 15h4" />
    </>
  ),
  marketplace: (
    <>
      <path d="M4 9h16l-1.2-4H5.2z" />
      <path d="M5 9v11h14V9" />
      <path d="M9.5 20v-6h5v6" />
    </>
  ),
};

interface Stat {
  end: number;
  suffix: string;
  decimals?: number;
  label: string;
}

const stats: Stat[] = [
  { end: 50, suffix: '+', label: 'Modules' },
  { end: 99.9, suffix: '%', decimals: 1, label: 'Uptime' },
  { end: 15, suffix: '+', label: 'Verticals' },
];

const CountUp = ({
  end,
  suffix = '',
  decimals = 0,
  active,
  duration = 1500,
}: {
  end: number;
  suffix?: string;
  decimals?: number;
  active: boolean;
  duration?: number;
}) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * end);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, end, duration]);
  return (
    <>
      {value.toFixed(decimals)}
      {suffix}
    </>
  );
};

const HeadlessSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (event: React.MouseEvent<HTMLLIElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    el.style.setProperty('--my', `${event.clientY - rect.top}px`);
  };

  return (
    <div
      ref={sectionRef}
      className={`${styles['new-container']} ${
        revealed ? styles['revealed'] : ''
      }`}
    >
      <div className={styles['left-col']}>
        <p className={styles['eyebrow']}>Headless commerce platform</p>
        <h2 className={styles['main-heading']}>
          Business Agnostic Headless solution for all{' '}
          <span className={homeStyles['span-gradient']}>B2B &amp; B2C</span>
        </h2>
        <p className={styles['subtext']}>
          A fully modular, API-first commerce platform built to power any
          business model — from wholesale B2B portals to high-velocity B2C
          storefronts and everything in between.
        </p>

        {/* credibility stats */}
        <div className={styles['stat-strip']}>
          {stats.map((stat) => (
            <div className={styles['stat']} key={stat.label}>
              <div className={styles['stat-value']}>
                <CountUp
                  end={stat.end}
                  suffix={stat.suffix}
                  decimals={stat.decimals}
                  active={revealed}
                />
              </div>
              <div className={styles['stat-label']}>{stat.label}</div>
            </div>
          ))}
        </div>

        <ul className={styles['feature-list']}>
          {features.map((feature, index) => (
            <li
              key={index}
              className={styles['feature-item']}
              onMouseMove={handleMouseMove}
              style={{ '--i': index } as React.CSSProperties}
            >
              <span className={styles['item-glow']} aria-hidden="true" />
              <span className={styles['check-icon']}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {featureIcons[feature.icon]}
                </svg>
              </span>
              <span className={styles['feature-text']}>{feature.label}</span>
            </li>
          ))}
        </ul>

        <Link href="/product-stack" className={styles['cta-link']}>
          <Button text="Know More" />
        </Link>
      </div>

      <div className={styles['right-col']}>
        <div className={styles['mockup-wrapper']}>
          <span className={styles['glow']} aria-hidden="true" />

          <DashboardMockup />

          {/* live floating badges */}
          <div className={`${styles['badge']} ${styles['badge-revenue']}`}>
            <span className={styles['badge-trend']}>▲</span>
            <div>
              <div className={styles['badge-value']}>+15%</div>
              <div className={styles['badge-label']}>Revenue</div>
            </div>
          </div>
          <div className={`${styles['badge']} ${styles['badge-live']}`}>
            <span className={styles['badge-dot']} />
            <div>
              <div className={styles['badge-value']}>8.8k</div>
              <div className={styles['badge-label']}>Live users</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeadlessSection;
